import { Alert, Box, Button, MenuItem, Paper, Typography } from '@mui/material'
import AgendaView from './View'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { ReactComponent as Boxes } from '../../assets/icons/boxes.svg'
import { useStyles } from './styles'
import ModalLayout from '../../ui/modals/layout'
import useTranslations from '../../i18n/useTranslations'
import { useCallback, useEffect, useState } from 'react'
import AgendaTable from './TableAgenda/View'

import { useUserContext } from '../../auth/UserProvider'
import Loader from '../../ui/views/Loader'
import SearchInput from '../ShippingsTracking/SearchInput'
import { format } from 'date-fns'
import SplitButton from '../../ui/components/splitButton'
import Select from '../../ui/components/select'
import {
  useAddressBookByCenter,
  useAddressBookSearch,
  useAddressBookTracking,
  useAddressBookTrackingProgress,
  useDeleteAddressMutation,
  useDeleteAddressesMutation,
  useManifestFiles,
} from '../../services/addressBook'
import AddEditDirection from './AddEditDirection'
import AgendaTypeSwitcherView, {
  TRACKING_OPTIONS,
} from './AgendaTypeSwitcherView'
import FileUploadModal from './FileUploadModal'
import SuccessfulImportedModal from './SuccessfulImportedModal'
import { ReactComponent as Upload } from '../../assets/icons/upload.svg'
import { ReactComponent as Plus } from '../../assets/icons/person-add.svg'
import useShippingActions from '../ShippingsTracking/TrackingTable/useShippingActions'
import ImportedContactsTable from './TableImportedContacts/View'
import { useGetFileDetailMutation } from '../../services/shippingsRecording'

const useSelectableContact = contacts => {
  const [selected, _setSelected] = useState([])

  const toggleSelection = useCallback(
    contactsInfo => {
      if (!!selected.find(x => x?.id === contactsInfo?.id)) {
        _setSelected(selectedContacts => [
          ...selectedContacts.filter(
            selectedShipping => selectedShipping?.id !== contactsInfo?.id,
          ),
        ])
      } else {
        _setSelected(selectedContacts => [
          ...selectedContacts,
          { ...contactsInfo },
        ])
      }
    },
    [selected],
  )

  const toggleAll = useCallback(
    isChecked => {
      if (isChecked) {
        const nextSelected = contacts.map(({ data }) => {
          return { ...data }
        })
        const difference = selected.filter(
          contact => !nextSelected.some(({ id }) => id === contact?.id),
        )
        _setSelected([...difference])
      } else {
        const nextSelected = contacts.map(({ data }) => {
          return { ...data }
        })
        const difference = nextSelected.filter(contact =>
          selected.filter(({ id }) => id !== contact?.id),
        )
        _setSelected([...difference, ...selected])
      }
    },
    [contacts, selected],
  )

  const clearSelection = useCallback(() => {
    _setSelected([])
  }, [])

  return { selected, toggleSelection, toggleAll, clearSelection }
}

const EmptyState = ({ selectedTracking, emptySearch, onClickImport }) => {
  const classes = useStyles()
  const t = useTranslations()
  return selectedTracking === TRACKING_OPTIONS.agenda ? (
    <Paper className={clsx(classes.paper, classes.emptyState)}>
      {emptySearch ? (
        <>
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('empty:no-results')}
          </Typography>
          <Typography>
            <span className={classes.secondaryText}>
              {t('empty:no-results-search')}
            </span>
          </Typography>
        </>
      ) : (
        <>
          <Boxes />
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('agenda:empty-state')}
          </Typography>
        </>
      )}
    </Paper>
  ) : (
    <Paper className={clsx(classes.paper, classes.emptyState)}>
      {emptySearch ? (
        <>
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('empty:no-results')}
          </Typography>
          <Typography>
            <span className={classes.secondaryText}>
              {t('empty:no-results-search')}
            </span>
          </Typography>
        </>
      ) : (
        <>
          <Boxes />
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('agenda:massive-request')}
          </Typography>
          <Typography>
            <span
              onClick={onClickImport}
              className={classes.primaryText}
              style={{ cursor: 'pointer' }}
            >
              {t('agenda:import-your-file')}
            </span>
            <span className={classes.secondaryText}>
              {t('agenda:save-time')}
            </span>
          </Typography>
        </>
      )}
    </Paper>
  )
}

const Agenda = () => {
  const t = useTranslations()
  const classes = useStyles()
  const [centerSelected, setCenterSelected] = useState(null)
  const [centerToImport, setCenterToImport] = useState(null)
  const [addressToDelete, setAddressToDelete] = useState()
  const [addEditAddress, setAddEditAddress] = useState()
  const [filesToUpload, setFilesToUpload] = useState([])
  const [intervalID, setIntervalID] = useState()
  const [uploadFileModal, setUploadFileModal] = useState(false)
  const { exportContactsToExcel } = useShippingActions()
  const [selectedTracking, setSelectedTracking] = useState(
    TRACKING_OPTIONS.agenda,
  )
  const [extensionAndName, setExtensionandName] = useState({
    extension: 'pdf',
    name: 'file',
  })
  const [manifestStatus, setManifestStatus] = useState()

  const [showAlert, setShowAlert] = useState()
  const [tableParams, setTableParams] = useState({
    search: '',
    page: 0,
    rowsPerPage: 10,
    totalRows: 28,
    dateFrom: format(
      new Date(Date.now()).setMonth(new Date().getMonth() - 3),
      'yyyy-MM-dd',
    ),
    dateTo: format(
      new Date(Date.now()).setDate(new Date().getDate() + 1),
      'yyyy-MM-dd',
    ),
  })

  const {
    isLoading: isLoadingFiles,
    data: filesData,
    refetch: refetchTracking,
    setData: setFilesData,
  } = useAddressBookTracking({
    filters: {
      dateFrom: tableParams.dateFrom,
      dateTo: tableParams.dateTo,
      page: tableParams.page,
      rowsPerPage: tableParams.rowsPerPage,
      sortType: tableParams.sortType,
      sortColumn: tableParams.sortColumn,
      search: tableParams.search,
    },
  })

  const {
    isLoading: isLoadingFilesProgress,
    data: filesDataProgress,
    refetch: refetchTrackingProgress,
    setData: setFilesDataProgress,
  } = useAddressBookTrackingProgress({
    filters: {
      dateFrom: tableParams.dateFrom,
      dateTo: tableParams.dateTo,
      page: tableParams.page,
      rowsPerPage: tableParams.rowsPerPage,
      sortType: tableParams.sortType,
      sortColumn: tableParams.sortColumn,
      search: tableParams.search,
    },
  })

  const refetchFiles = async () => {
    try {
      const newData = await refetchTracking({
        filters: {
          dateFrom: tableParams.dateFrom,
          dateTo: tableParams.dateTo,
          page: tableParams.page,
          rowsPerPage: tableParams.rowsPerPage,
          sortType: tableParams.sortType,
          sortColumn: tableParams.sortColumn,
          search: tableParams.search,
        },
      })
      if (setFilesData) setFilesData(newData)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const refetchFilesInProgress = async () => {
    try {
      const newData = await refetchTrackingProgress({
        filters: {
          dateFrom: tableParams.dateFrom,
          dateTo: tableParams.dateTo,
          page: tableParams.page,
          rowsPerPage: tableParams.rowsPerPage,
          sortType: tableParams.sortType,
          sortColumn: tableParams.sortColumn,
          search: tableParams.search,
        },
      })

      const map = Object.fromEntries(
        newData?.data?.data.map(item => [item.jobId, item]),
      )
      const newDataProgress = filesData?.data.map(item => {
        const update = map[item.jobId]
        if (!update) return item
        return {
          ...item,
          status: update.status,
          numberOfSucceededLines: update.numberOfSucceededLines,
          numberOfFailedLines: update.numberOfFailedLines,
          errorsFile: update.errorsFile,
        }
      })

      if (setFilesData)
        setFilesData({
          ...newData,
          data: {
            data: newDataProgress,
            pagination: newData?.data?.pagination,
          },
        })
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const { centres, clientCode, clientId } = useUserContext()

  const manifestFilesMutation = useManifestFiles(
    () => {
      setManifestStatus('success')
      setCenterToImport(null)
    },
    () => {
      setManifestStatus('error')
      setCenterToImport(null)
    },
  )

  const addressesQuery = useAddressBookByCenter(
    {
      centreCode: centerSelected?.id,
      pageLimit: tableParams.rowsPerPage,
      pageOffset: tableParams.page,
      sortColumn: tableParams?.sortColumn,
      sortType: tableParams?.sortType,
    },
    { enabled: tableParams.search === '' },
  )

  const addressesSearchQuery = useAddressBookSearch({
    centreCode: centerSelected?.id,
    pageLimit: tableParams.rowsPerPage,
    pageOffset: tableParams.page,
    searchFilterValue: tableParams.search,
    sortColumn: tableParams?.sortColumn,
    sortType: tableParams?.sortType,
  })

  const query =
    tableParams.search === '' ? addressesQuery : addressesSearchQuery
  const { data: addresses, isFetching: isLoadingContacts } = query ?? []

  const onSuccessDelete = () => {
    query.refetch()
    setAddressToDelete({ delete: true })
    setShowAlert({
      text: t('action:delete-address-my-agenda-body-success'),
      severity: 'success',
    })
  }

  const onSuccessDeleteMultiple = () => {
    query.refetch()
    setAddressToDelete({ delete: selectedContactInfo })
    setShowAlert({
      text:
        selectedContactInfo?.length > 1
          ? t('action:delete-addresses-my-agenda-body-success')
          : t('action:delete-address-my-agenda-body-success'),
      severity: 'success',
    })
  }

  const onErrorDelete = () => {
    setShowAlert({
      text: t('action:delete-address-my-agenda-body-error'),
      severity: 'error',
    })
  }

  const deleteAddressMutation = useDeleteAddressMutation(
    onSuccessDelete,
    onErrorDelete,
  )
  const deleteAddressesMutation = useDeleteAddressesMutation(
    onSuccessDeleteMultiple,
    onErrorDelete,
  )

  const handleSubmitDelete = clientAddressCode => {
    deleteAddressMutation.mutate({
      clientCenterCode: centerSelected?.id,
      clientAddressCode: clientAddressCode,
    })
  }

  const downloadCSV = async data => {
    try {
      const binaryData = atob(data?.data)
      const arrayBuffer = new ArrayBuffer(binaryData.length)
      const uint8Array = new Uint8Array(arrayBuffer)

      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i)
      }

      const blob = new Blob([uint8Array], {
        type: `application/${extensionAndName?.extension}`,
      })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${extensionAndName?.name}.${extensionAndName?.extension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getFileDetail = useGetFileDetailMutation(downloadCSV)

  const {
    selected: selectedContactInfo,
    toggleSelection: toggleContactSelection,
    toggleAll: toggleAllContacts,
    clearSelection: clearContactSelection,
  } = useSelectableContact(addresses?.data)

  const handleSubmitMultipleDelete = () => {
    deleteAddressesMutation.mutate({
      clientCenterCode: centerSelected?.id,
      clientAddressesCode: selectedContactInfo,
    })
  }

  const splitButtonOptions = [
    {
      text:
        selectedContactInfo.length > 1
          ? t('action:remove-contacts', {
              contacts: selectedContactInfo?.length,
            })
          : t('action:remove-contact'),
      onClick: () => setAddressToDelete(selectedContactInfo),
      isActive: true,
    },
    {
      text:
        selectedContactInfo.length > 1
          ? t('action:export-contacts')
          : t('action:export-contact'),
      onClick: () => exportContactsToExcel(selectedContactInfo),
      isActive: true,
    },
  ]

  const handleAddFile = document => {
    setFilesToUpload([...filesToUpload, ...document])
  }

  const handleRemoveFile = id => {
    const newFiles = [...filesToUpload]
    const index = newFiles.findIndex(element => element.id === id)
    filesToUpload.splice(index, 1)
    setFilesToUpload([...filesToUpload])
  }

  const resetVariables = () => {
    setShowAlert(null)
    setUploadFileModal(false)
    setManifestStatus()
    setShowAlert(null)
    setFilesToUpload([])
  }

  const handleSubmitGetDetail = async (id, provider, extension, name) => {
    await setExtensionandName({ extension: extension, name: `${name}_errors` })
    getFileDetail.mutate({
      clientCode: clientId,
      id: id,
      provider: provider,
    })
  }

  useEffect(() => {
    setTableParams({
      ...tableParams,
      totalRows:
        selectedTracking === TRACKING_OPTIONS.import
          ? filesData?.pagination?.record_count
          : addresses?.pagination?.totalCount,
    })
  }, [filesData, addresses, selectedTracking])

  useEffect(() => {
    if (centres?.length) setCenterSelected(centres[0])
  }, [centres])

  useEffect(() => {
    const fetchData = async () => {
      await refetchFiles()
    }

    fetchData()
  }, [
    clientCode,
    tableParams.rowsPerPage,
    tableParams.search,
    tableParams.dateFrom,
    tableParams.dateTo,
    tableParams.page,
    tableParams.sortColumn,
    tableParams.sortType,
  ])

  useEffect(() => {
    const fetchData = async () => {
      await refetchFiles()
    }
    if (manifestStatus === 'success') fetchData()
  }, [manifestStatus])

  useEffect(() => {
    let intervalId
    let pendingOrProcessing = []
    if (filesData?.data?.length) {
      filesData?.data?.forEach(item => {
        if (
          item.status === 'created' ||
          item.status === 'in-progress' ||
          (item.status === 'has-errors' && !item?.errorsFile?.s3Key)
        ) {
          pendingOrProcessing.push(item)
        }
      })
    }
    if (!intervalID && pendingOrProcessing?.length) {
      intervalId = setInterval(async () => {
        await refetchFilesInProgress(true)
      }, 5000)
      setIntervalID(intervalId)
    } else if (!pendingOrProcessing?.length) {
      clearInterval(intervalID)
      setIntervalID(null)
    }
  }, [filesData, intervalID, refetchFilesInProgress])

  const renderDeleteModal = () => {
    return addressToDelete && !addressToDelete?.delete ? (
      <ModalLayout
        isOpen={addressToDelete && !addressToDelete?.delete}
        title={
          Array.isArray(addressToDelete) && addressToDelete?.length > 1
            ? t('action:delete-addresses-my-agenda-header')
            : t('action:delete-address-my-agenda-header')
        }
        content={
          <>
            {Array.isArray(addressToDelete) && addressToDelete?.length > 1
              ? t('action:delete-addresses-my-agenda-body', {
                  number: addressToDelete?.length,
                })
              : t('action:delete-address-my-agenda-body')}

            <>
              {showAlert?.severity === 'error' ? (
                <Alert
                  severity={showAlert?.severity}
                  className={classes.alertError}
                >
                  <Typography component="p" variant="body2">
                    {showAlert?.text}
                  </Typography>
                </Alert>
              ) : null}
            </>
          </>
        }
        actions={
          <>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => {
                setShowAlert(null)
                setAddressToDelete(null)
              }}
            >
              {t('action:cancel')}
            </Button>

            <Button
              size="small"
              disabled={
                deleteAddressMutation?.isLoading ||
                deleteAddressesMutation?.isLoading
              }
              onClick={() => {
                if (Array.isArray(addressToDelete)) handleSubmitMultipleDelete()
                else handleSubmitDelete(addressToDelete)
              }}
            >
              {showAlert?.severity === 'error'
                ? t('action:retry')
                : t('action:confirm')}
            </Button>
          </>
        }
      />
    ) : null
  }

  const renderDeleteSuccessModal = () => {
    return (
      <ModalLayout
        isOpen={addressToDelete?.delete}
        title={
          Array.isArray(addressToDelete?.delete) &&
          addressToDelete?.delete?.length > 1
            ? t('action:delete-addresses-my-agenda-header')
            : t('action:delete-address-my-agenda-header')
        }
        content={t(
          showAlert?.text ?? 'action:delete-address-my-agenda-body-success',
        )}
        actions={
          <>
            <Button
              size="small"
              onClick={() => {
                setAddressToDelete(null)
                clearContactSelection()
              }}
            >
              {t('action:close')}
            </Button>
          </>
        }
      />
    )
  }

  const renderAddEditModal = () => {
    return (
      <ModalLayout
        isOpen={addEditAddress && !addEditAddress?.edit}
        title={
          addEditAddress?.id
            ? t('agenda:edit-contact')
            : t('agenda:add-contact')
        }
        wide={500}
        content={
          <AddEditDirection
            onSuccessAddContact={() => {
              setAddEditAddress({ add: true })
              setShowAlert({
                severity: 'success',
                text: t('agenda:add-success'),
              })
              query.refetch()
            }}
            onErrorAddContact={() => {
              setAddEditAddress({ add: true })
              setShowAlert({ severity: 'error', text: t('agenda:add-error') })
            }}
            onSuccessEditContact={() => {
              setAddEditAddress({ edit: true })
              setShowAlert({
                severity: 'success',
                text: t('agenda:edit-success'),
              })
              query.refetch()
            }}
            onErrorEditContact={() => {
              setAddEditAddress({ edit: true })
              setShowAlert({
                severity: 'error',
                text: t('agenda:edit-error'),
              })
            }}
            onCancel={() => setAddEditAddress(null)}
            addEditAddress={addEditAddress}
            clientCenterCode={centerSelected?.id}
          />
        }
      />
    )
  }

  const renderAddEditSuccessModal = () => {
    return (
      <ModalLayout
        isOpen={addEditAddress?.edit || addEditAddress?.add}
        title={
          addEditAddress?.edit
            ? t('agenda:edit-contact')
            : t('agenda:add-contact')
        }
        content={t(showAlert?.text ?? 'agenda:edit-success')}
        actions={
          <>
            <Button
              size="small"
              onClick={() => {
                setAddEditAddress(null)
              }}
            >
              {t('action:close')}
            </Button>
          </>
        }
      />
    )
  }

  const renderImportModal = () => {
    return !manifestStatus ? (
      <ModalLayout
        onClose={() => {
          setFilesToUpload([])
          setUploadFileModal(false)
        }}
        isOpen={uploadFileModal}
        wide={true}
        content={
          <Box className={classes.modalContainer}>
            <FileUploadModal
              handleRemoveFile={handleRemoveFile}
              handleAddFile={handleAddFile}
              openModal={setUploadFileModal}
              filesToUpload={filesToUpload}
              manifestFiles={manifestFilesMutation}
              centerSelected={centerToImport}
              setCenterSelected={setCenterToImport}
            />
          </Box>
        }
      />
    ) : (
      <SuccessfulImportedModal
        isOpen={manifestStatus === 'success'}
        onClose={resetVariables}
      />
    )
  }

  //REFETCH IMPORTED

  return (
    <>
      <AgendaView>
        {renderImportModal()}
        {renderDeleteModal()}
        {renderDeleteSuccessModal()}
        {renderAddEditModal()}
        {renderAddEditSuccessModal()}

        <Box className={classes.rowInputs}>
          {selectedTracking === TRACKING_OPTIONS.agenda ? (
            <Select
              label={t('agenda:select-center')}
              className={classes.centerToImport}
              value={centerSelected}
              onChange={event => {
                setCenterSelected(event.target.value)
              }}
            >
              {centres.map(e => {
                return (
                  <MenuItem key={e} value={e}>
                    <em>{`${e?.id} | ${e?.name}`}</em>
                  </MenuItem>
                )
              })}
            </Select>
          ) : null}
          <Box className={classes.rowButtons}>
            {selectedTracking === TRACKING_OPTIONS.agenda &&
            (addresses?.data?.length || tableParams.search?.length) ? (
              <SearchInput
                className={classes.searchInput}
                queryValue={tableParams.search}
                noHelperText={true}
                placeholder={t(
                  'placeholder:search-by-name-or-address-or-company',
                )}
                search={value => {
                  setTableParams({ ...tableParams, search: value })
                }}
                clearSearch={() => {
                  setTableParams({ ...tableParams, search: '' })
                }}
              />
            ) : null}
            <Button
              onClick={() => {
                setSelectedTracking(TRACKING_OPTIONS.import)
                setUploadFileModal(true)
              }}
              size={'small'}
              startIcon={<Upload />}
              variant="outlined"
              className={classes.actionButton}
            >
              {t('agenda:import-contacts')}
            </Button>
            <Button
              onClick={() => {
                setSelectedTracking(TRACKING_OPTIONS.agenda)
                setAddEditAddress(true)
              }}
              size={'small'}
              startIcon={<Plus />}
              className={classes.actionButton}
            >
              {t('agenda:add-contact')}
            </Button>
          </Box>
        </Box>

        <AgendaTypeSwitcherView
          changeSelectedTracking={setSelectedTracking}
          selectedTracking={selectedTracking}
          incidentsCount={10}
          unscheduledPickupsCount={10}
        >
          {isLoadingContacts || isLoadingFiles || !addresses?.data ? (
            <Loader />
          ) : addresses?.data?.length &&
            selectedTracking === TRACKING_OPTIONS.agenda ? (
            <>
              <AgendaTable
                showCheckbox={true}
                showMenu={true}
                onClickDeleteContact={contactInfo =>
                  setAddressToDelete(contactInfo?.id)
                }
                rows={addresses?.data ?? []}
                handleSubmitDelete={() => {}}
                handleSubmitGetDetail={() => {}}
                tableParams={tableParams}
                onClickEditContact={setAddEditAddress}
                onChangeTableParams={setTableParams}
                onCheckboxChange={toggleContactSelection}
                onSelectAllClick={toggleAllContacts}
                selectedRows={selectedContactInfo}
              />
              {selectedContactInfo.length > 0 ? (
                <>
                  <Box
                    sx={{ justifyContent: 'flex-end', display: 'flex', mb: 2 }}
                  >
                    <SplitButton size="medium" options={splitButtonOptions} />
                  </Box>
                </>
              ) : null}
            </>
          ) : selectedTracking === TRACKING_OPTIONS.import &&
            filesData?.data?.length ? (
            <ImportedContactsTable
              rows={filesData?.data ?? []}
              handleSubmitDelete={() => {}}
              handleSubmitGetDetail={handleSubmitGetDetail}
              tableParams={tableParams}
              onChangeTableParams={setTableParams}
            />
          ) : (
            <EmptyState
              selectedTracking={selectedTracking}
              emptySearch={tableParams.search?.length}
              onClickImport={() => setUploadFileModal(true)}
            />
          )}
        </AgendaTypeSwitcherView>
      </AgendaView>
    </>
  )
}

Agenda.propTypes = {}
EmptyState.propTypes = {
  onClickImport: PropTypes.func,
  selectedTracking: PropTypes.func,
  emptySearch: PropTypes.bool,
}

export default Agenda
